import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import styled from 'styled-components';

import { COLORS } from '../../../utils/theme/colors';
import Arrow from '../../atoms/icons/Arrow';
import { usePagination, DOTS } from '../../molecules/Pagination/usePagination';
import { closeAllCards } from '../../organisms/CardListConstructor/helpers';

interface IListPagination {
  callback: any;
  pureData: any[] | undefined;
  ownRef: any;
  pagination?: any;
}

export const ListPagination = forwardRef<HTMLUListElement, IListPagination>(
  (props, ref) => {
    const { callback, pureData, ownRef, pagination } = props;

    const maxContentPerPage = 10;
    const siblingCount = 1;

    const length = pagination?.total || pureData?.length || 0;
    const maxPagesNumber = Math.ceil(Number(length) / maxContentPerPage);

    const [selected, setSelected] = useState(pagination?.page || 1);
    const [isInitialized, setIsInitialized] = useState(false);

    const paginationRange = usePagination({
      currentPage: selected,
      totalCount: length,
      siblingCount,
      pageSize: maxContentPerPage,
    });

    const lastPage = paginationRange?.[paginationRange.length - 1];

    const separatePages = useCallback(() => {
      const pagesDivided: any[] = [];
      let start = 0;
      let end = maxContentPerPage;

      for (let i = 0; i < maxPagesNumber; i++) {
        const sliced = pureData?.slice(start, end);
        pagesDivided.push(sliced);
        start += maxContentPerPage;
        end += maxContentPerPage;
      }

      return pagesDivided;
    }, [pureData, maxPagesNumber]);

    const paginate = useCallback(
      (index = selected || 1) => {
        if (index < 1 || maxPagesNumber < index) return;

        callback(separatePages()[index - 1]);
        setSelected(index);
        closeAllCards();
      },
      [callback, maxPagesNumber, selected, separatePages],
    );

    useEffect(() => {
      if (!isInitialized) {
        paginate(selected);
        setIsInitialized(true);
      }
    }, [paginate, selected, isInitialized]);

    useImperativeHandle(ownRef, () => ({ paginate }));

    if (selected === 0 || paginationRange?.length < 2) {
      return null;
    }

    const onNext = () => {
      const next = selected + 1;
      paginate(next);
      pagination?.move?.(next);
    };

    const onPrevious = () => {
      const prev = selected - 1;
      paginate(prev);
      pagination?.move?.(prev);
    };

    return (
      <PaginationWrapper ref={ref}>
        <li
          className={`pagination-item ${selected === 1 ? 'disabled' : ''}`}
          onClick={selected === 1 ? undefined : onPrevious}
        >
          <Arrow fill={'#444'} className='right_arrow' />
        </li>

        {paginationRange.map((pageNumber, index) => {
          if (pageNumber === DOTS) {
            return (
              <li key={`dots-${index}`} className='pagination-item dots'>
                &#8230;
              </li>
            );
          }

          return (
            <li
              key={`page-${pageNumber}`}
              className={`pagination-item ${
                selected === pageNumber ? 'selected' : ''
              }`}
              onClick={() => {
                paginate(pageNumber as number);
                pagination?.move?.(pageNumber);
              }}
            >
              {pageNumber}
            </li>
          );
        })}

        <li
          className={`pagination-item ${
            selected === lastPage ? 'disabled' : ''
          }`}
          onClick={selected === lastPage ? undefined : onNext}
        >
          <Arrow fill={'#444'} className='left_arrow' />
        </li>
      </PaginationWrapper>
    );
  },
);

ListPagination.displayName = 'ListPagination';

const PaginationWrapper = styled.ul`
  display: flex;
  list-style-type: none;
  flex-direction: row;
  margin-top: 10px;
  margin-bottom: 0px;
  justify-content: flex-end;
  align-items: center;
  user-select: none;

  .pagination-item {
    background-color: ${COLORS.transparentDark};
    height: 35px;
    width: 35px;
    border-radius: 100%;
    margin: 0 5px;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: background-color 200ms ease-in-out;
    cursor: pointer;

    &:hover {
      background-color: ${({ theme }) => theme.hoveredDiv};
      color: ${COLORS.primary};
    }

    &:hover path {
      stroke: ${COLORS.primary};
    }

    &.selected {
      background-color: ${({ theme }) => theme.hoveredDiv};
      color: ${COLORS.primary};
    }

    &.dots {
      cursor: default;
    }

    &.disabled {
      pointer-events: none;
      cursor: default;

      svg path {
        stroke: rgba(0, 0, 0, 0.43);
      }

      &:hover {
        background-color: transparent;
      }
    }

    .left_arrow {
      transform: rotate(180deg);
    }
  }
`;
